import { Box } from "@chakra-ui/react"
import { createContext, useContext, type RefObject } from "react"
import { PackageFragment } from "@pathwright/graphql-client/src/generated"
import { getResolvedPackageTheme } from "./theme"
import {
  PackageButton,
  PackageColorMode,
  PackageDataSource,
  PackageThemeOverrides,
  PackageVariant,
  ResolvedPackageTheme
} from "./types"
import { usePackage } from "./usePackage"

export type PackageProviderProps = {
  children: React.ReactNode
  variant: PackageVariant
  actionButton?: PackageButton
  dataSource?: PackageDataSource
  themeOverrides?: PackageThemeOverrides
  colorMode?: PackageColorMode
  portalContainer?: RefObject<HTMLElement | null>
}

type PackageContextType = Omit<
  PackageProviderProps,
  "children" | "themeOverrides"
> & {
  packageData: PackageFragment | null
  loading: boolean
  error?: Error | null
  theme: ResolvedPackageTheme
}

const PackageContext = createContext<PackageContextType>({
  variant: "tag",
  dataSource: "url",
  colorMode: "dark",
  theme: {
    primaryColor: "",
    backgroundColor: "",
    textColor: "",
    secondaryTextColor: ""
  },
  actionButton: {
    label: undefined,
    href: undefined,
    target: undefined,
    render: undefined,
    show: true
  },
  packageData: null,
  loading: false,
  error: null
})

export const usePackageContext = () => useContext(PackageContext)

export const packageFocusVisibleOnlyStyles = {
  "& :where(button, [role='button'], a[href], input, select, textarea, [tabindex]:not([tabindex='-1'])):focus:not(:focus-visible):not(.focus-visible)":
    {
      outline: "none",
      boxShadow: "none"
    },
  "& :where(button, [role='button'], a[href], input, select, textarea, [tabindex]:not([tabindex='-1']))[data-focus]:not([data-focus-visible]):not(.focus-visible)":
    {
      outline: "none",
      boxShadow: "none"
    }
} as const

const PackageProvider = ({
  children,
  actionButton,
  dataSource,
  variant,
  themeOverrides,
  colorMode,
  portalContainer
}: PackageProviderProps) => {
  const { packageData, loading, error } = usePackage({
    dataSource
  })

  if (loading) {
    // For now, hide Spinner since we won't always show package details
    // when loading finishes, leads to odd UX.
    return null
    // return <Spinner size="sm" />
  }

  if (error || !packageData) {
    return actionButton?.render ? actionButton.render() : null
  }

  return (
    <ResolvedPackageProvider
      variant={variant}
      actionButton={actionButton}
      dataSource={dataSource}
      themeOverrides={themeOverrides}
      colorMode={colorMode}
      portalContainer={portalContainer}
      packageData={packageData}
    >
      {children}
    </ResolvedPackageProvider>
  )
}

export const ResolvedPackageProvider = ({
  children,
  actionButton,
  dataSource,
  variant,
  themeOverrides,
  colorMode,
  portalContainer,
  packageData
}: PackageProviderProps & { packageData: PackageFragment }) => {
  const resolvedTheme = getResolvedPackageTheme({
    packageData,
    themeOverrides,
    colorMode
  })

  return (
    <PackageContext.Provider
      value={{
        variant,
        dataSource,
        theme: resolvedTheme,
        colorMode,
        portalContainer,
        packageData,
        actionButton,
        loading: false,
        error: null
      }}
    >
      <Box display="contents" css={packageFocusVisibleOnlyStyles}>
        {children}
      </Box>
    </PackageContext.Provider>
  )
}

export default PackageProvider
